import { AuditLog, AuditMessage, AuditNotification } from '../types';
import { apiFetch } from './apiClient';

function fetchAudit<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(`/audit${endpoint}`, options);
}

export interface ChannelAuditResponse {
  channel: { id: number; name: string; type: string };
  messages: AuditMessage[];
  total: number;
  auditLogId: number;
}

export interface UserAuditResponse {
  userId: string;
  messages: AuditMessage[];
  total: number;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
}

/** 대화 내용 열람 요청(승인 절차 — server/services/auditAccess.js) */
export type AuditAccessStatus = 'pending' | 'approved' | 'rejected' | 'canceled' | 'expired';

export interface AuditAccessRequest {
  id: number;
  requesterId: string;
  requesterName: string | null;
  requesterRole: string | null;
  targetType: 'channel' | 'user';
  targetId: string;
  targetName: string | null;
  reason: string;
  status: AuditAccessStatus;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  validHours: number | null;
  validUntil: string | null;
  createdAt: string;
}

export interface AuditAccessListResponse {
  requests: AuditAccessRequest[];
  /** Super Admin이면 true — 승인·반려 버튼을 그린다(최종 판정은 서버) */
  canDecide: boolean;
  /** 내 사번(요청자 본인 구분용) */
  me: string;
  /** Super Admin에게만 — 내가 결정할 수 있는 대기 건수 */
  pendingCount?: number;
}

export interface IntegrityResponse {
  valid: boolean;
  totalChecked: number;
  firstInvalidId?: number;
}

export const auditService = {
  // 대화 내용 열람 요청 — 다른 Super Admin이 승인해야 열람할 수 있다
  async requestAccess(targetType: 'channel' | 'user', targetId: string, reason: string): Promise<AuditAccessRequest> {
    const r = await fetchAudit<{ request: AuditAccessRequest }>('/access-requests', {
      method: 'POST',
      body: JSON.stringify({ targetType, targetId, reason }),
    });
    return r.request;
  },

  // 열람 요청 목록 — Super Admin은 all(전체), 감사자는 내 요청만
  async listAccessRequests(scope: 'mine' | 'all' = 'all'): Promise<AuditAccessListResponse> {
    return fetchAudit<AuditAccessListResponse>(`/access-requests?scope=${scope}`);
  },

  async approveAccess(id: number, hours: number, note?: string): Promise<AuditAccessRequest> {
    const r = await fetchAudit<{ request: AuditAccessRequest }>(`/access-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ hours, note }),
    });
    return r.request;
  },

  async rejectAccess(id: number, note: string): Promise<AuditAccessRequest> {
    const r = await fetchAudit<{ request: AuditAccessRequest }>(`/access-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
    return r.request;
  },

  async cancelAccess(id: number): Promise<AuditAccessRequest> {
    const r = await fetchAudit<{ request: AuditAccessRequest }>(`/access-requests/${id}/cancel`, { method: 'POST' });
    return r.request;
  },

  // 채널 메시지 감사 조회 — 승인된 열람 요청(requestId)이 필요하다. 사유는 서버가 요청에서 가져온다
  async getChannelMessages(
    channelId: number,
    requestId: number,
    limit = 50,
    offset = 0
  ): Promise<ChannelAuditResponse> {
    return fetchAudit<ChannelAuditResponse>(
      `/channels/${channelId}/messages?requestId=${requestId}&limit=${limit}&offset=${offset}`
    );
  },

  // 사용자 메시지 감사 조회 — 승인된 열람 요청(requestId)이 필요하다
  async getUserMessages(
    userId: string,
    requestId: number,
    limit = 50,
    offset = 0,
    channelId?: number
  ): Promise<UserAuditResponse> {
    let url = `/users/${encodeURIComponent(userId)}/messages?requestId=${requestId}&limit=${limit}&offset=${offset}`;
    if (channelId) url += `&channelId=${channelId}`;
    return fetchAudit<UserAuditResponse>(url);
  },

  // 감사 로그 조회
  async getLogs(filters?: {
    auditorId?: string;
    actionType?: string;
    targetType?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLogsResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return fetchAudit<AuditLogsResponse>(`/logs?${params.toString()}`);
  },

  // 무결성 검증
  async verifyIntegrity(): Promise<IntegrityResponse> {
    return fetchAudit<IntegrityResponse>('/integrity');
  },

  // 읽지 않은 알림 조회
  async getNotifications(): Promise<AuditNotification[]> {
    return fetchAudit<AuditNotification[]>('/notifications');
  },

  // 알림 읽음 처리
  async markNotificationRead(notificationId: number): Promise<void> {
    await fetchAudit(`/notifications/${notificationId}/read`, { method: 'POST' });
  },

  // 전체 알림 읽음 처리
  async markAllNotificationsRead(): Promise<void> {
    await fetchAudit('/notifications/read-all', { method: 'POST' });
  },
};
